import React, { useState } from 'react';
import { TrendingUp, Package, Gift, Calendar, Users, Zap, Check } from 'lucide-react';
import theme from '../theme';

const PORTFOLIO_TYPES = [
  {
    id: 'revenue_sharing',
    name: 'Revenue Sharing',
    icon: TrendingUp,
    description:
      'Set aside a defined portion of sales or program revenue for future Kickback activity, rewards, or holder benefits based on your approved setup.',
    color: '#6C5CE7',
    features: [
      'Connect revenue rules to this Kickback',
      'Track contribution activity',
      'Prepare future holder benefit options',
    ],
  },
  {
    id: 'managed',
    name: 'Kickback Architect Managed',
    icon: Users,
    description:
      'Request support from an approved Kickback Architect to help review, structure, or prepare your Kickback before launch.',
    color: '#0984E3',
    features: [
      'Guided setup review',
      'Strategy and structure support',
      'Optional expert assistance',
    ],
  },
  {
    id: 'merch',
    name: 'Product Utility',
    icon: Package,
    description:
      'Attach product-based benefits, access, proof materials, or special perks connected to a physical or digital item.',
    color: '#00B894',
    features: [
      'Product-linked holder benefits',
      'Authenticity support materials',
      'Exclusive product access',
    ],
  },
  {
    id: 'sweepstakes',
    name: 'Drawings & Reward Events',
    icon: Gift,
    description:
      'Create holder-based reward events, drawings, or special access opportunities when permitted by your rules and applicable requirements.',
    color: '#FDCB6E',
    features: [
      'Reward-event setup',
      'Holder eligibility rules',
      'Clear entry and access terms',
    ],
  },
  {
    id: 'event',
    name: 'Event Pass / Ticket',
    icon: Calendar,
    description:
      'Give holders access to events, meetups, launches, digital sessions, or special experiences connected to this Kickback.',
    color: '#E17055',
    features: [
      'Event access setup',
      'VIP or community experiences',
      'Time-based access rules',
    ],
  },
  {
    id: 'subscription',
    name: 'Subscription Utility',
    icon: Zap,
    description:
      'Provide recurring holder benefits, member access, content access, or ongoing perks connected to this Kickback.',
    color: '#A29BFE',
    features: [
      'Recurring benefit setup',
      'Member access rules',
      'Ongoing perk management',
    ],
  },
];

const PortfolioTypeSelector = ({ onSelect, allowMultiple = true, selectedTypes = [] }) => {
  // Batch 1A final correction — accept both `id` values (e.g. `revenue_sharing`)
  // and display names (e.g. `Revenue Sharing`) so hydration from the persisted
  // `portfolio_type` / `portfolio_types` field always highlights the correct
  // card. Also stays reactive to prop changes (previous version froze the
  // initial selection at mount, so edit-mode hydration never updated).
  const normalize = React.useCallback((incoming) => {
    const arr = Array.isArray(incoming) ? incoming : (incoming ? [incoming] : []);
    return arr
      .map((v) => {
        if (!v) return null;
        const t = PORTFOLIO_TYPES.find(
          (x) => x.id === v || x.name.toLowerCase() === String(v).toLowerCase(),
        );
        return t ? t.id : null;
      })
      .filter(Boolean);
  }, []);

  const [selected, setSelected] = useState(() => normalize(selectedTypes));

  // Rehydrate whenever the parent supplies a new persisted selection.
  React.useEffect(() => {
    const next = normalize(selectedTypes);
    setSelected((prev) => (
      prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next
    ));
  }, [selectedTypes, normalize]);

  const toggleType = (typeId) => {
    let newSelected;
    if (allowMultiple) {
      newSelected = selected.includes(typeId)
        ? selected.filter((id) => id !== typeId)
        : [...selected, typeId];
    } else {
      newSelected = [typeId];
    }
    
    setSelected(newSelected);
    if (onSelect) {
      onSelect(newSelected);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <h3
          data-testid="kickback-type-heading"
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: theme.colors.text.primary,
            marginBottom: '8px',
          }}
        >
          Select Kickback Type
        </h3>
        <p style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
          Choose one or more Kickback types. This helps us guide your setup, required details, and launch-readiness checklist.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
        {PORTFOLIO_TYPES.map((type) => {
          const isSelected = selected.includes(type.id);
          const Icon = type.icon;

          return (
            <button
              key={type.id}
              data-testid={`portfolio-type-${type.id}`}
              onClick={() => toggleType(type.id)}
              style={{
                padding: '20px',
                border: `2px solid ${isSelected ? type.color : theme.colors.borders}`,
                borderRadius: theme.radius.md,
                background: isSelected ? `${type.color}10` : '#FFFFFF',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              {isSelected && (
                <div
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: type.color,
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Check size={14} color="#FFFFFF" />
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: theme.radius.sm,
                    background: `${type.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={24} style={{ color: type.color }} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: theme.colors.text.primary,
                      marginBottom: '2px',
                    }}
                  >
                    {type.name}
                  </div>
                </div>
              </div>

              <p
                style={{
                  fontSize: '13px',
                  color: theme.colors.text.secondary,
                  lineHeight: '1.5',
                  marginBottom: '12px',
                }}
              >
                {type.description}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {type.features.map((feature, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontSize: '12px',
                      color: theme.colors.text.secondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <div
                      style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        background: type.color,
                      }}
                    />
                    {feature}
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {selected.length > 1 && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#FFF9E6',
            border: '1px solid #FFD700',
            borderRadius: theme.radius.sm,
          }}
        >
          <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.6' }}>
            <strong>Hybrid Portfolio:</strong> You've selected {selected.length} types. This creates a multi-utility
            portfolio combining features from each type.
          </p>
        </div>
      )}
    </div>
  );
};

export default PortfolioTypeSelector;
